'use client'

import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'motion/react'
import Marquee from 'react-fast-marquee'

import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { poppins } from '~/font'
import Photo1 from './images/testimonial-1.webp'
import Photo2 from './images/testimonial-2.webp'
import Photo3 from './images/testimonial-3.webp'
import Photo4 from './images/testimonial-4.webp'
import Photo5 from './images/testimonial-5.webp'
import Photo6 from './images/testimonial-6.webp'
import Photo7 from './images/testimonial-7.webp'

type TestimonialItem =
  | { type: 'text'; name: string; role: string; quote: string }
  | { type: 'image'; photo: typeof Photo1 }

const row1: TestimonialItem[] = [
  {
    type: 'text',
    name: 'Iis Delis',
    role: 'Guru SD Negeri Serang 10',
    quote:
      'Salah satu momen bermakna yang saya alami adalah ketika seorang siswa yang biasanya pendiam akhirnya berani mengemukakan pendapatnya tentang cerita buku yang dibaca di depan teman-temannya.',
  },
  { type: 'image', photo: Photo1 },
  {
    type: 'text',
    name: 'Azqia Wardatulummah',
    role: 'Guru SDN Sindangsari',
    quote:
      "Saya jadi paham bahwa setiap anak punya 'gerbang' literasi yang berbeda, dan tugas guru adalah membantu mereka menemukannya.",
  },
  {
    type: 'text',
    name: 'Titi Suryanti',
    role: 'Guru SD Negeri Sepring',
    quote:
      'Bagi saya, pengalaman ini menjadi pengingat untuk selalu menghadirkan pembelajaran yang sederhana, dekat dengan kehidupan sehari-hari, dan bermanfaat jangka panjang bagi siswa.',
  },
  { type: 'image', photo: Photo2 },
  { type: 'image', photo: Photo3 },
]

const row2: TestimonialItem[] = [
  { type: 'image', photo: Photo4 },
  { type: 'image', photo: Photo5 },
  {
    type: 'text',
    name: 'Fandi Ahmad',
    role: 'Guru SD Negeri Serang 10',
    quote:
      'Awalnya ada orang tua yang ragu, tapi setelah melihat anaknya membaca 1–2 jam sehari di PiBo, mereka jadi mendukung penuh. Sampai ada yang bilang kalau anaknya membantu ibunya mendongengkan adiknya yang masih kecil.',
  },
  { type: 'image', photo: Photo6 },
  { type: 'image', photo: Photo7 },
  {
    type: 'text',
    name: 'Dewi Anita',
    role: 'SD Taman',
    quote:
      'Saya dapat berkolaborasi dengan wali murid… banyak pengalaman selain membaca bersama yaitu anak-anak berdiskusi dan terlibat aktif.',
  },
]

function TextCard({ name, role, quote }: { name: string; role: string; quote: string }) {
  return (
    <div className='bg-neutral-white mx-2 flex h-54 w-80 flex-col gap-2 rounded-2xl p-4 drop-shadow-sm'>
      <div>
        <p className={cn(typographyVariants({ variant: 'title-lg' }), 'text-neutral-black')}>{name}</p>
        <p className={cn(typographyVariants({ variant: 'label-sm' }), 'text-grey-100')}>{role}</p>
      </div>
      <p className={cn(typographyVariants({ variant: 'body-md' }), 'text-neutral-black')}>{quote}</p>
    </div>
  )
}

function ImageCard({ photo }: { photo: typeof Photo1 }) {
  return (
    <div className='relative mx-2 size-54 overflow-hidden rounded-2xl'>
      <Image src={photo} alt='Foto kelas PiBo' fill draggable={false} className='object-cover select-none' />
    </div>
  )
}

function renderItem(item: TestimonialItem, key: number) {
  if (item.type === 'text') return <TextCard key={key} name={item.name} role={item.role} quote={item.quote} />
  return <ImageCard key={key} photo={item.photo} />
}

interface TestimonialsProps {
  className?: string
}

export function Testimonials({ className }: TestimonialsProps) {
  return (
    <section className={cn('flex w-full flex-col gap-2 overflow-hidden py-6', poppins.className, className)}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className='mx-auto flex w-full max-w-7xl flex-col items-center gap-4 px-6 text-center'
      >
        <h2 className={cn(typographyVariants({ variant: 'headline-lg' }), 'text-neutral-black')}>
          Didukung oleh banyak cinta
        </h2>
        <p className={cn(typographyVariants({ variant: 'body-lg' }), 'text-neutral-black')}>
          Sekolah, keluarga, dan anak-anak adalah jantung dari PiBo. Berikut adalah cuplikan dari senyuman yang kami
          lihat setiap hari. 💚
        </p>
      </motion.div>

      <section className='flex w-full flex-col gap-4 py-4'>
        <Marquee speed={60} pauseOnHover pauseOnClick autoFill className='py-2'>
          {row1.map((item, i) => renderItem(item, i))}
        </Marquee>

        <Marquee speed={50} direction='right' pauseOnHover pauseOnClick autoFill className='py-2'>
          {row2.map((item, i) => renderItem(item, i))}
        </Marquee>
      </section>

      <div className='flex justify-center'>
        <Button variant='outline' asChild>
          <Link href='/blog/kisah-sukses'>Lihat lebih banyak senyum</Link>
        </Button>
      </div>
    </section>
  )
}
